import { Share2 } from 'lucide-react';
import React from 'react';

import PnlShareDialog, {
    type PnlShareRequest,
} from '@/features/trading/components/PnlShareDialog';
import { tokenAmount } from '@/features/trading/utils/tradeFormat';
import { type PublicTapeTrade, USDC_DECIMALS } from '@/lib/api/speed';
import { cn } from '@/lib/utils';

import { pnlCardFromTapeTrade } from './pnlCard';
import { pseudonymFor } from './pseudonym';
import { markForTrade, roiPercent, unrealizedPnl } from './unrealized';
import type { LiveTapeState } from './useLiveTape';

/** Stable identity so a default-valued `marks` prop is not a new Map each render. */
const EMPTY_MARKS: Map<string, number> = new Map();

/** Terminal outcome badges, matching the ones the history tab uses. */
const OUTCOME: Record<string, { label: string; className: string }> = {
    TakeProfit: { label: 'TP', className: 'text-success' },
    StopLoss: { label: 'SL', className: 'text-destructive' },
    Liquidated: { label: 'LIQ', className: 'text-destructive' },
};

/** `+9.05%`. Two decimals like the terminal's own PnL cells. */
function formatPercent(value: number | null): string | null {
    if (value === null || !Number.isFinite(value)) return null;
    return `${value >= 0 ? '+' : '-'}${Math.abs(value).toFixed(2)}%`;
}

function formatUsd(value: string | null): string | null {
    if (value === null) return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    return `${parsed >= 0 ? '+' : '-'}$${Math.abs(parsed).toFixed(2)}`;
}

// margin is USDC base units (6 decimals) on the row, the seed and the socket
// alike; pnl and prices are plain decimals. Same split the history tab handles.
function formatMargin(raw: string | null): string {
    const amount = tokenAmount(raw, USDC_DECIMALS);
    if (amount === null) return '—';
    return `$${amount.toFixed(2)}`;
}

// one format for a $0.14 market and a $100k one
function formatPrice(value: string | null): string {
    if (value === null) return '—';
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '—';
    if (parsed >= 1000) {
        return parsed.toLocaleString('en-US', { maximumFractionDigits: 2 });
    }
    if (parsed >= 1) return parsed.toFixed(2);
    return parsed.toPrecision(4);
}

/** An empty tape, a broken one and a filtered-out one must not read the same. */
function emptyMessage(tape: LiveTapeState, filtered: boolean): string {
    if (tape.isLoading) return 'Loading the tape…';
    if (tape.isError) return 'The live tape is unavailable right now.';
    // The feed is working and the viewer removed everything in it — telling
    // them nothing has traded would be a lie they cannot act on.
    if (filtered) return 'No trades match these filters.';
    return 'No trades yet — the next one lands here.';
}

/**
 * One trade, as a stranger sees it.
 *
 * Rows are static snapshots on purpose. The positions table's live cells each
 * open a store subscription and describe *the viewer's own* position — pointed
 * at someone else's trade they would be both wrong and a subscription per row.
 */
const TapeRow: React.FC<{
    trade: PublicTapeTrade;
    marks: Map<string, number>;
    onSelectTrader?: (adapterAddress: string) => void;
    onShare?: (trade: PublicTapeTrade) => void;
}> = ({ trade, marks, onSelectTrader, onShare }) => {
    const who = pseudonymFor(trade.adapterAddress);
    const outcome = OUTCOME[trade.status];
    const isOpen = trade.status === 'Open';

    // An open row has no realized pnl, so it is priced against the current
    // mark. Null when that market is not listed or the row is missing a size —
    // the cell falls back to the bare OPEN it showed before.
    const live = isOpen
        ? unrealizedPnl(trade, markForTrade(trade, marks))
        : null;
    const figure = isOpen ? live : Number(trade.pnl);
    const pnl = isOpen
        ? live === null
            ? null
            : formatUsd(String(live))
        : formatUsd(trade.pnl);
    const isProfit = (figure ?? 0) >= 0;
    // return on the collateral they put up, which is the only way a 3x and a
    // 40x row on the same feed are comparable
    const percent = formatPercent(roiPercent(figure ?? null, trade.margin));
    // an unrealized figure is ALWAYS approximate (no funding, none of our
    // fees); a realized one is only approximate before the reconciler lands
    const approximate = isOpen || trade.pnlEstimated;

    return (
        <div className="tape-row grid items-center border-t border-border/40 px-4 py-1.5 font-mono text-xs tabular-nums transition-colors hover:bg-secondary/50">
            {/* who. The name is the way into that trader's own tape — the
                pseudonym is already a handle the eye follows down the feed, so
                it is the thing a viewer reaches for when they want more of one. */}
            <button
                type="button"
                onClick={() => onSelectTrader?.(trade.adapterAddress)}
                aria-label={`Show only ${who.name}'s trades`}
                className="tape-who flex min-w-0 items-center gap-1.5 text-left transition-colors hover:text-primary"
            >
                <span aria-hidden className="shrink-0">
                    {who.emoji}
                </span>
                <span className="truncate font-sans font-semibold text-foreground">
                    {who.name}
                </span>
                <span className="shrink-0 text-[9px] text-muted-foreground">
                    {who.short}
                </span>
            </button>

            {/* what */}
            <span className="tape-what flex min-w-0 items-center gap-1.5">
                <span className="truncate font-sans font-semibold">
                    {trade.coin?.toUpperCase() ?? '—'}
                </span>
                <span
                    className={cn(
                        'shrink-0 rounded px-1 py-px text-[8px] font-bold uppercase',
                        trade.isLong
                            ? 'bg-primary/15 text-primary'
                            : 'bg-destructive/15 text-destructive',
                    )}
                >
                    {trade.isLong ? 'Long' : 'Short'}
                </span>
                {outcome && (
                    <span
                        className={cn(
                            'shrink-0 text-[9px] font-semibold uppercase tracking-[0.08em]',
                            outcome.className,
                        )}
                    >
                        {outcome.label}
                    </span>
                )}
                {/* "Open" used to be the result cell's text. It moved here so
                    that cell can carry the number, and so open/closed stays
                    legible beside the other state badges rather than being
                    inferred from whether an exit price is present. */}
                {isOpen && (
                    <span className="shrink-0 text-[9px] font-semibold uppercase tracking-[0.08em] text-primary">
                        Open
                    </span>
                )}
            </span>

            <span className="tape-lev text-foreground/70">
                {trade.leverage !== null ? `${trade.leverage}x` : '—'}
            </span>
            <span className="tape-size text-foreground/80">
                {formatMargin(trade.margin)}
            </span>
            <span className="tape-px truncate text-foreground/70">
                {formatPrice(trade.entryPx)}
                {trade.closePx !== null && (
                    <span className="text-muted-foreground">
                        {' → '}
                        {formatPrice(trade.closePx)}
                    </span>
                )}
            </span>

            {/* how it went */}
            <span className="tape-result flex items-center justify-end gap-1 sm:w-36">
                {pnl !== null ? (
                    <span
                        className={cn(
                            // stacked on a phone, where the row's first line is
                            // shared with the trader's name; side by side once
                            // the page has the width for it. One markup, so the
                            // figure is never in the DOM twice.
                            'flex flex-col items-end leading-tight sm:flex-row sm:items-baseline sm:gap-1',
                            isProfit ? 'text-success' : 'text-destructive',
                            // an open trade's figure is a moving estimate, not a
                            // settled one; dimmed so a closed row still reads as
                            // the authoritative number on the page
                            isOpen && 'opacity-80',
                        )}
                        title={
                            isOpen
                                ? 'Unrealized, at the current mark. Excludes funding and fees.'
                                : undefined
                        }
                    >
                        <span className="font-bold">
                            {/* ≈ covers both approximations: an open trade
                                priced against the mark, and a manual close
                                broadcast before the reconciler computed the
                                real number */}
                            {approximate ? '≈' : ''}
                            {pnl}
                        </span>
                        {/* absent when the row records no margin — a percentage
                            of nothing is not 0% */}
                        {percent !== null && (
                            <span className="text-[10px] opacity-80">
                                {percent}
                            </span>
                        )}
                    </span>
                ) : (
                    // Unpriceable: an unlisted market, or a row that reached
                    // the tape before its fill size. A dash, not a second
                    // "Open" — the badge beside the side already says that,
                    // and "$0.00" would be a confident lie.
                    <span className="text-muted-foreground">—</span>
                )}
                {/* same rule as the history tab: no figure, nothing to share */}
                {onShare && pnl !== null && (
                    <button
                        type="button"
                        title="Share this PnL"
                        aria-label={`Share ${who.name}'s ${trade.coin?.toUpperCase() ?? ''} PnL`}
                        onClick={() => onShare(trade)}
                        className="shrink-0 p-0.5 text-muted-foreground transition-colors hover:text-primary"
                    >
                        <Share2 size={11} />
                    </button>
                )}
            </span>
        </div>
    );
};

/**
 * The public tape's body: a column header and its rows.
 *
 * It fills whatever it is given rather than sizing itself — the Live Trades
 * page hands it the viewport under a fixed header, and the page owns the
 * scroll container. Density is deliberate at page size too: a feed is read by
 * scanning, and spaced-out rows put fewer trades in front of the eye.
 */
export const LiveTapeRows: React.FC<{
    tape: LiveTapeState;
    /** mark price per uppercased coin; an empty map just means no open figures */
    marks?: Map<string, number>;
    /** the rows to draw. Defaults to the whole tape; the page passes the
     *  filtered subset, which is why the empty state has to be told why. */
    trades?: readonly PublicTapeTrade[];
    filtered?: boolean;
    onSelectTrader?: (adapterAddress: string) => void;
}> = ({
    tape,
    marks = EMPTY_MARKS,
    trades = tape.trades,
    filtered = false,
    onSelectTrader,
}) => {
    const [share, setShare] = React.useState<{
        request: PnlShareRequest;
        trader: string;
    } | null>(null);

    // Built at click time: an open row's card is a snapshot at that moment's
    // mark, and it must not re-price under the viewer while they download it.
    const openShare = React.useCallback(
        (trade: PublicTapeTrade) => {
            const data = pnlCardFromTapeTrade(trade, marks);
            if (!data) return;
            setShare({
                request: { kind: 'ready', data },
                trader: pseudonymFor(trade.adapterAddress).name,
            });
        },
        [marks],
    );

    return (
        <>
            {/* Column headers only make sense over single-line rows. On a phone
            each row is two lines and labels its own parts (the side badge, the
            `x` on the leverage, the `$` on the size), so the header is dropped
            rather than mislabelling the stacked layout. */}
            <div className="tape-row sticky top-0 z-10 hidden items-center bg-card px-4 py-1.5 text-[10px] font-semibold text-muted-foreground sm:grid">
                <span className="tape-who">Trader</span>
                <span className="tape-what">Market / Side</span>
                <span className="tape-lev">Lev</span>
                <span className="tape-size">Size</span>
                <span className="tape-px">Entry → Exit</span>
                <span className="tape-result w-36 text-right">Result</span>
            </div>
            {trades.length === 0 ? (
                <div className="flex items-center justify-center px-4 py-6 text-xs text-muted-foreground">
                    {emptyMessage(tape, filtered)}
                </div>
            ) : (
                trades.map((trade) => (
                    <TapeRow
                        key={trade.id}
                        trade={trade}
                        marks={marks}
                        onSelectTrader={onSelectTrader}
                        onShare={openShare}
                    />
                ))
            )}
            {/* Reaching the end of the list is the request for the next page. No
            button: scrolling to the bottom of a feed IS asking for more of it,
            and a tape that simply stops after fifty reads as fifty being all
            there has ever been.

            Hidden while a filter is narrowing the view: the predicate runs over
            the rows already held, so "load more" there would fetch global
            history to find a handful of matches, one page at a time. */}
            {!filtered && (tape.hasMore || tape.isLoadingMore) && (
                <LoadMoreSentinel onReach={tape.loadMore} />
            )}
            {share && (
                <PnlShareDialog
                    request={share.request}
                    onDismiss={() => setShare(null)}
                    title="Share this PnL"
                    subtitle={`${share.trader}'s ${share.request.kind === 'ready' ? share.request.data.coin : ''} trade, as a card.`}
                />
            )}
        </>
    );
};

/**
 * Asks for the next page when it scrolls into view.
 *
 * An observer rather than a scroll handler: the list is a plain overflow
 * container, and a scroll listener on it would run on every frame of every
 * scroll to answer a question the browser can answer for free.
 */
const LoadMoreSentinel: React.FC<{ onReach: () => void }> = ({ onReach }) => {
    const ref = React.useRef<HTMLDivElement | null>(null);
    const onReachRef = React.useRef(onReach);
    onReachRef.current = onReach;

    React.useEffect(() => {
        const node = ref.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    onReachRef.current();
                }
            },
            // A screen early, so the next page is usually there by the time
            // the reader arrives.
            { rootMargin: '400px' },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className="flex items-center justify-center px-4 py-5 text-xs text-muted-foreground"
        >
            Loading older trades…
        </div>
    );
};

export default LiveTapeRows;
